
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { PromoCodesManager } from "@/components/admin/PromoCodesManager";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/components/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { ServicesManager } from "@/components/admin/ServicesManager";
import { PortfolioManager } from "@/components/admin/PortfolioManager";
import { BookingsManager } from "@/components/admin/BookingsManager";

import { ReviewsManager } from "@/components/admin/ReviewsManager";
import { ProfileManager } from "@/components/admin/ProfileManager";
import { AdminSignupModal } from "@/components/admin/AdminSignupModal";
import { CategoriesManager } from "@/components/admin/CategoriesManager";
import { AuditLogViewer } from "@/components/admin/AuditLogViewer";
import { SocialLinksManager } from "@/components/admin/SocialLinksManager";
import { MessagesManager } from "@/components/admin/MessagesManager";
import { FooterManager } from "@/components/admin/FooterManager";
import { AnalyticsDashboard } from "@/components/admin/AnalyticsDashboard";
import { HeroBannerManager } from "@/components/admin/HeroBannerManager";
import { NotificationManager } from "@/components/admin/NotificationManager";
import { SalespeopleManager } from "@/components/admin/SalespeopleManager";
import { ReferralCodesManager } from "@/components/admin/ReferralCodesManager";
import { CommissionReports } from "@/components/admin/CommissionReports";
import { CommissionPayments } from "@/components/admin/CommissionPayments";
import { FinancialOverview } from "@/components/admin/FinancialOverview";
import { AdminsManager } from "@/components/admin/AdminsManager";
import {
  Camera,
  Users,
  Calendar,
  Star,
  ImageIcon,
  LogOut,
  BarChart3,
  Settings,
  UserPlus,
  Shield,
  ShieldCheck,
  Tag,
  Share2,
  Mail,
  MessageSquare,
  Image as ImageSlideIcon,
  AlertTriangle,
  Gift,
  DollarSign,
  TrendingUp,
  Receipt
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

const Admin = () => {
  const { user, signOut, role: userRole } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [stats, setStats] = useState({
    services: 0,
    bookings: 0,
    reviews: 0,
    portfolioItems: 0,
    pendingCommissions: 0
  });
  const [topSalespeople, setTopSalespeople] = useState<any[]>([]);
  const [recentReferrals, setRecentReferrals] = useState<any[]>([]);
  const [shouldThrowError, setShouldThrowError] = useState(false);

  if (shouldThrowError) {
    throw new Error("This is a test error for PostHog tracking verification!");
  }

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [servicesRes, bookingsRes, reviewsRes, portfolioRes] = await Promise.all([
          supabase.from('services').select('id', { count: 'exact', head: true }),
          supabase.from('bookings').select('id', { count: 'exact', head: true }),
          supabase.from('reviews').select('id', { count: 'exact', head: true }),
          supabase.from('portfolio_albums').select('id', { count: 'exact', head: true })
        ]);

        setStats(prev => ({
          ...prev,
          services: servicesRes.count || 0,
          bookings: bookingsRes.count || 0,
          reviews: reviewsRes.count || 0,
          portfolioItems: portfolioRes.count || 0
        }));
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };

    const fetchRecentActivity = async () => {
      try {
        const { data } = await supabase
          .from('admin_audit_log')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(5);

        setRecentActivity(data || []);
      } catch (error) {
        console.error('Error fetching recent activity:', error);
      }
    };

    const fetchReferralStats = async () => {
      try {
        // Fetch pending commissions
        const { data: salespeopleData } = await (supabase
          .from('salespeople' as any)
          .select('total_commission_earned, total_commission_paid')
          .eq('is_active', true) as any);

        const pendingTotal = (salespeopleData || []).reduce(
          (sum: number, p: any) => sum + ((p.total_commission_earned || 0) - (p.total_commission_paid || 0)),
          0
        );

        // Fetch top salespeople
        const { data: topData } = await (supabase
          .from('salespeople' as any)
          .select('name, total_commission_earned')
          .order('total_commission_earned', { ascending: false })
          .limit(3) as any);

        // Fetch recent referrals
        const { data: redemptionsData } = await (supabase
          .from('referral_code_redemptions' as any)
          .select(`
            id, 
            customer_email, 
            commission_amount, 
            redeemed_at,
            salespeople (name)
          `)
          .order('redeemed_at', { ascending: false })
          .limit(5) as any);

        setStats(prev => ({ ...prev, pendingCommissions: pendingTotal }));
        setTopSalespeople(topData || []);
        setRecentReferrals(redemptionsData || []);
      } catch (error) {
        console.error('Error fetching referral stats:', error);
      }
    };

    fetchStats();
    fetchRecentActivity();
    fetchReferralStats();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: "Signed out",
        description: "You have been successfully signed out.",
      });
      navigate("/");
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-auto min-h-16 items-center justify-between px-4 sm:px-6 py-3 sm:py-0">
          <div className="flex items-center gap-2 sm:gap-3">
            <Camera className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
            <div>
              <h1 className="text-base sm:text-lg md:text-xl font-bold">Admin Dashboard</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <p className="text-xs sm:text-sm text-muted-foreground truncate max-w-[150px] sm:max-w-none">
                  {user?.email}
                </p>
                {userRole && (
                  <Badge variant={userRole === 'super_admin' ? 'default' : 'secondary'} className="h-4 text-[10px] px-1.5 font-bold uppercase tracking-wider">
                    {userRole === 'super_admin' ? (
                      <ShieldCheck className="h-2.5 w-2.5 mr-1" />
                    ) : (
                      <Shield className="h-2.5 w-2.5 mr-1" />
                    )}
                    {userRole.replace('_', ' ')}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSignupModal(true)}
              className="hidden sm:flex"
            >
              <UserPlus className="h-4 w-4 mr-0 sm:mr-2" />
              <span className="hidden md:inline">Add Admin</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/")}
              className="hidden md:flex"
            >
              View Website
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSignOut}
            >
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Sign Out</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Services</CardTitle>
              <Settings className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.services}</div>
              <p className="text-xs text-muted-foreground">
                Active photography services
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Bookings</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.bookings}</div>
              <p className="text-xs text-muted-foreground">
                Total booking requests
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Reviews</CardTitle>
              <Star className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.reviews}</div>
              <p className="text-xs text-muted-foreground">
                Customer reviews
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Portfolio</CardTitle>
              <ImageIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.portfolioItems}</div>
              <p className="text-xs text-muted-foreground">
                Portfolio albums
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <TabsList className="inline-flex w-auto min-w-full lg:w-auto lg:grid lg:grid-cols-12 h-auto flex-nowrap lg:flex-wrap p-1 gap-1">
              <TabsTrigger value="overview" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Overview</TabsTrigger>
              <TabsTrigger value="analytics" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Analytics</TabsTrigger>
              <TabsTrigger value="financial" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Financial</TabsTrigger>
              <TabsTrigger value="services" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Services</TabsTrigger>
              <TabsTrigger value="categories" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Categories</TabsTrigger>
              <TabsTrigger value="portfolio" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Portfolio</TabsTrigger>
              <TabsTrigger value="hero" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Hero Slides</TabsTrigger>
              <TabsTrigger value="bookings" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Bookings</TabsTrigger>
              <TabsTrigger value="promo-codes" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Promo Codes</TabsTrigger>
              <TabsTrigger value="salespeople" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Salespeople</TabsTrigger>
              <TabsTrigger value="referral-codes" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Referral Codes</TabsTrigger>
              <TabsTrigger value="commission-reports" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Commission Reports</TabsTrigger>
              <TabsTrigger value="commission-payments" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Payments</TabsTrigger>
              <TabsTrigger value="reviews" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Reviews</TabsTrigger>
              <TabsTrigger value="social" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Social Links</TabsTrigger>
              <TabsTrigger value="messages" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Messages</TabsTrigger>
              <TabsTrigger value="footer" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Footer</TabsTrigger>
              <TabsTrigger value="notifications" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Notifications</TabsTrigger>
              <TabsTrigger value="audit" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Audit Log</TabsTrigger>
              {userRole === 'super_admin' && (
                <TabsTrigger value="admins" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">
                  <ShieldCheck className="h-3 w-3 mr-1" /> Admins
                </TabsTrigger>
              )}
              <TabsTrigger value="profile" className="text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3">Profile</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                  <CardDescription>
                    Common administrative tasks
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    className="w-full justify-start"
                    onClick={() => setActiveTab("services")}
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Manage Services
                  </Button>
                  <Button
                    className="w-full justify-start"
                    variant="outline"
                    onClick={() => setActiveTab("portfolio")}
                  >
                    <ImageIcon className="h-4 w-4 mr-2" />
                    Manage Portfolio
                  </Button>
                  <Button
                    className="w-full justify-start"
                    variant="outline"
                    onClick={() => setActiveTab("bookings")}
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    View Bookings
                  </Button>
                  <Button
                    className="w-full justify-start"
                    variant="outline"
                    onClick={() => setActiveTab("messages")}
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    View Messages
                  </Button>
                  <Button
                    className="w-full justify-start"
                    variant="outline"
                    onClick={() => setActiveTab("social")}
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Social Links
                  </Button>
                  <Button
                    className="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-50"
                    variant="outline"
                    onClick={() => setShouldThrowError(true)}
                  >
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Trigger Test Error
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Recent Activity</CardTitle>
                  <CardDescription>
                    Latest updates and activities
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm">
                    {recentActivity.length > 0 ? (
                      recentActivity.map((activity) => (
                        <div key={activity.id} className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <div className="flex-1">
                            <span className="font-medium">{activity.admin_email}</span>
                            <span className="text-muted-foreground"> {activity.action.toLowerCase().replace(/_/g, ' ')} </span>
                            <span>{activity.entity_type.replace(/_/g, ' ')}</span>
                            <div className="text-xs text-muted-foreground">
                              {new Date(activity.created_at).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span>No recent activity</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Commission & Referral Widgets */}
              <Card className="border-orange-200 bg-orange-50/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-orange-600" />
                    Pending Payouts
                  </CardTitle>
                  <CardDescription>Total commission due to salespeople</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-orange-600">
                    {formatCurrency(stats.pendingCommissions)}
                  </div>
                  <Button
                    variant="link"
                    className="p-0 h-auto text-xs mt-2"
                    onClick={() => setActiveTab("commission-payments")}
                  >
                    Go to Payments →
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    Top Performance
                  </CardTitle>
                  <CardDescription>Top earners by commission</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {topSalespeople.map((person, i) => (
                      <div key={i} className="flex justify-between items-center text-sm">
                        <span className="font-medium text-muted-foreground">{person.name}</span>
                        <span className="font-bold">{formatCurrency(person.total_commission_earned)}</span>
                      </div>
                    ))}
                    {topSalespeople.length === 0 && <p className="text-sm text-muted-foreground">No data yet</p>}
                  </div>
                </CardContent>
              </Card>

              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    Recent Referrals
                  </CardTitle>
                  <CardDescription>Latest referral code redemptions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {recentReferrals.length > 0 ? (
                      <div className="grid gap-4">
                        {recentReferrals.map((ref) => (
                          <div key={ref.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
                            <div className="space-y-1">
                              <p className="text-sm font-medium">{ref.customer_email}</p>
                              <p className="text-xs text-muted-foreground">
                                Referred by <span className="font-semibold text-foreground">{(ref.salespeople as any)?.name}</span>
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold text-green-600">+{formatCurrency(ref.commission_amount)}</p>
                              <p className="text-[10px] text-muted-foreground">
                                {new Date(ref.redeemed_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">No recent referrals recorded.</p>
                    )}
                    <Button
                      variant="outline"
                      className="w-full text-xs"
                      onClick={() => setActiveTab("commission-reports")}
                    >
                      View All Redemption Reports
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsDashboard />
          </TabsContent>

          <TabsContent value="financial">
            <FinancialOverview />
          </TabsContent>

          <TabsContent value="services">
            <ServicesManager />
          </TabsContent>

          <TabsContent value="categories">
            <CategoriesManager />
          </TabsContent>

          <TabsContent value="portfolio">
            <PortfolioManager />
          </TabsContent>

          <TabsContent value="hero">
            <HeroBannerManager />
          </TabsContent>

          <TabsContent value="bookings">
            <BookingsManager />
          </TabsContent>

          <TabsContent value="promo-codes">
            <PromoCodesManager />
          </TabsContent>

          <TabsContent value="salespeople">
            <SalespeopleManager />
          </TabsContent>

          <TabsContent value="referral-codes">
            <ReferralCodesManager />
          </TabsContent>

          <TabsContent value="commission-reports">
            <CommissionReports />
          </TabsContent>

          <TabsContent value="commission-payments">
            <CommissionPayments />
          </TabsContent>

          <TabsContent value="reviews">
            <ReviewsManager />
          </TabsContent>

          <TabsContent value="social">
            <SocialLinksManager />
          </TabsContent>

          <TabsContent value="messages">
            <MessagesManager />
          </TabsContent>

          <TabsContent value="footer" className="space-y-4">
            <FooterManager />
          </TabsContent>
          <TabsContent value="notifications" className="space-y-4">
            <NotificationManager />
          </TabsContent>

          <TabsContent value="audit">
            <AuditLogViewer />
          </TabsContent>

          {userRole === 'super_admin' && (
            <TabsContent value="admins">
              <AdminsManager />
            </TabsContent>
          )}

          <TabsContent value="profile">
            <ProfileManager />
          </TabsContent>
        </Tabs>
      </div>

      <AdminSignupModal
        open={showSignupModal}
        onOpenChange={setShowSignupModal}
      />
    </div>
  );
};

export default Admin;